import { createAsyncThunk } from '@reduxjs/toolkit';
import { adjustStockRequest, getStockRequest, transferStockRequest } from './stockApi';
import { selectStockGroupBy } from './stockSelectors';
import { transferableBuckets } from './stockTransferUtils';
import { adjustableBuckets } from './stockAdjustUtils';
import { ALL_FILTER } from './stockConstants';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the page of balances the filters describe.
 *
 * The warehouse comes from the module's context rather than from the filters:
 * it is chosen once for every screen. Consolidating happens on the server
 * because, paginated, adding up in the browser would add up the visible page.
 *
 * @returns {Promise<{count: number, results: Array<object>, totals: object, grouped_by: string|null}>}
 */
export const fetchStock = createAsyncThunk(
  'stock/fetchStock',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { company, state: stockState, search, includeZero, page, rowsPerPage } =
        state.stock.filters;

      const params = {
        page,
        page_size: rowsPerPage,
      };

      const warehouseId = selectCurrentWarehouseId(state);
      if (warehouseId) params.warehouse = warehouseId;
      if (company !== ALL_FILTER) params.company = company;
      if (stockState !== ALL_FILTER) params.state = stockState;
      if (search) params.search = search;
      if (includeZero) params.include_zero = '1';

      const groupBy = selectStockGroupBy(state);
      if (groupBy) params.group_by = groupBy;

      const response = await getStockRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching stock:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el inventario'));
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                               Store / move                                 */
/* -------------------------------------------------------------------------- */

/**
 * Where a product can be moved out of, in one warehouse.
 *
 * Asked apart from `fetchStock` so it does not replace what the person is
 * looking at. The origin is picked among the locations that really hold the
 * product, not among all of them: asking somebody to guess where there is some
 * is how phantom transfers happen.
 *
 * @param {{productId: number, warehouseId: number}} params
 * @returns {Promise<{productId: number, buckets: Array<object>}>}
 */
export const fetchTransferBuckets = createAsyncThunk(
  'stock/fetchTransferBuckets',
  async ({ productId, warehouseId }, { rejectWithValue }) => {
    try {
      const response = await getStockRequest({
        warehouse: warehouseId,
        product: productId,
        page_size: 100,
      });
      return { productId, buckets: transferableBuckets(response.data?.results || []) };
    } catch (error) {
      console.error('Error fetching transfer buckets:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo consultar dónde hay stock'));
    }
  }
);

/**
 * Moves stock between two locations and reloads the balances, with the same
 * filters and the same view that are on screen.
 *
 * @param {object} payload - See `buildTransferPayload`.
 */
export const transferStock = createAsyncThunk(
  'stock/transferStock',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await transferStockRequest(payload);
      dispatch(fetchStock());
      return response.data;
    } catch (error) {
      console.error('Error transferring stock:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo trasladar el stock'));
    }
  }
);

/**
 * Where a product sits, for the "Ajustar" dialog: its available stock in the
 * warehouse in use, location by location, the unplaced included. Apart from
 * the table for the same reason as `fetchTransferBuckets`.
 *
 * @param {{productId: number, warehouseId: number}} params
 * @returns {Promise<{productId: number, buckets: Array<object>}>}
 */
export const fetchAdjustBuckets = createAsyncThunk(
  'stock/fetchAdjustBuckets',
  async ({ productId, warehouseId }, { rejectWithValue }) => {
    try {
      const response = await getStockRequest({
        warehouse: warehouseId,
        product: productId,
        page_size: 100,
      });
      return { productId, buckets: adjustableBuckets(response.data?.results || []) };
    } catch (error) {
      console.error('Error fetching adjust buckets:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo consultar dónde hay stock'));
    }
  }
);

/**
 * Writes a manual adjustment and reloads the balances, with the same filters
 * and the same view that are on screen.
 *
 * @param {object} payload - See `buildAdjustPayload`.
 */
export const adjustStock = createAsyncThunk(
  'stock/adjustStock',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await adjustStockRequest(payload);
      dispatch(fetchStock());
      return response.data;
    } catch (error) {
      console.error('Error adjusting stock:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo ajustar el inventario'));
    }
  }
);
